// React
import { useEffect, useMemo, useRef, useContext } from 'react'

// Libraries
import { isEqual, isEmpty } from 'lodash'

// Hooks
import { useFormikContext } from 'formik'
import { useCachedQuery } from 'shared/utils/useCachedQuery'
import { useTranslation } from 'shared/utils/useTranslation'
import { useSettings } from 'shared/utils/useSettings'

// Shared
import { Format as FormatUtils } from 'back_office/v2/utils/utils'
import { fetchDeliveryOptionsRequest } from './requests'
import { CartPickerContext } from 'store/modules/cart/CartPicker'

/**
 * @function useCartPicker
 *
 * @returns {import('store/modules/cart/CartPicker').CartPickerContextValue}
 */
export const useCartPicker = () => {
  const context = useContext(CartPickerContext)

  if (!context) {
    throw Error('"useCartPicker" should be used within "CartPickerProvider"')
  }

  return context
}

export const useDeliveryOptions = ({ cart, fetchAvailability, onError, resetError }) => {
  const isFirstMount = useRef(true)

  const { i18n } = useTranslation('user')

  const form = useFormikContext()

  const requireDeliveryAddress = useSettings('store.require_delivery_address_when_selecting_rental_period')

  const formattedDeliveryAddress = useMemo(() => {
    if (!form.values.delivery_address) return

    return FormatUtils.address(form.values.delivery_address)
  }, [form.values.delivery_address])

  const { setLoading, save } = useCartPicker()

  const { perform: fetchDeliveryOptions, isLoading } = useCachedQuery(fetchDeliveryOptionsRequest, {
    key: formattedDeliveryAddress,
    errorKey: 'fetch_delivery_options_error',
    onSuccess: async ({ data }) => {
      resetError()

      const cart = data.included?.[0]?.attributes
      const options = data.data[0].attributes

      if (cart && form.values.start_location_id !== cart.start_location_id) {
        form.setFieldValue('start_location_id', cart.start_location_id)
        form.setFieldValue('stop_location_id', cart.stop_location_id)
      }

      if (options.rates.length === 0) {
        onError('delivery_unavailable_error')
      }
    },
    onError: ({ response }) => {
      const errors = response.data.errors

      let errorKey = 'delivery_unavailable_error'

      if (errors.length > 0) {
        errorKey = errors[0].meta?.delivery_address || 'delivery_unavailable_error'
      }

      if (!i18n.exists(errorKey)) {
        errorKey = 'delivery_unavailable_error'
      }

      onError(errorKey)
    },
    onLoading: () => {
      setLoading(true)
      resetError()
    },
    onSettled: () => {
      setLoading(false)
    }
  })

  const isDelivery = form.values.data?.fulfillment_type === 'delivery'

  useEffect(() => {
    const request = async () => {
      try {
        const { payload } = await save(form.values)

        await Promise.all([
          fetchDeliveryOptions({
            cartId: payload.response.cart.id,
            deliveryAddress: form.values.data.delivery_address
          }),
          fetchAvailability({ type: 'start', values: form.values })
        ])
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case 'fetch_delivery_options_error':
              onError('delivery_unavailable_error')
              break
            case 'fetch_availability_error':
              console.error('Error fetching availability', error)
              break
          }
        }
      }
    }

    if (!cart?.id) return
    if (!isDelivery) return
    if (!requireDeliveryAddress) return
    if (isEmpty(form.values.delivery_address)) return

    // The following fields are always required despite the selected country,
    // do not fetch the closest location if they are not set
    if (
      (!form.values.delivery_address?.country && !form.values.delivery_address?.country_id) ||
      !form.values.delivery_address?.address1 ||
      !form.values.delivery_address?.zipcode ||
      !form.values.delivery_address?.city
    )
      return

    // Check if the delivery address fields have no errors
    if (Object.keys(form.errors?.delivery_address ?? {}).length) return

    // Check if it's the first mount or if values have changed
    const valuesAreEqual =
      isEqual(form.values.delivery_address, form.initialValues.delivery_address) &&
      isEqual(form.values.start_location_id, form.initialValues.start_location_id) &&
      isEqual(form.values.stop_location_id, form.initialValues.stop_location_id)

    if (isFirstMount.current || !valuesAreEqual) {
      request()
    }

    isFirstMount.current = false
  }, [cart?.id, form.values, form.initialValues, form.errors, isDelivery, save, requireDeliveryAddress])

  return { isLoading }
}
